import type { SmartTimelineStep } from "@/data/portal/intelligence";
import { cn } from "@/lib/utils";
import {
  CheckCircle, Circle, AlertTriangle, Shield, User, Clock,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface Props {
  steps: SmartTimelineStep[];
}

export function SmartTimeline({ steps }: Props) {
  return (
    <div className="space-y-0">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        return (
          <div key={step.stage} className="flex gap-3">
            {/* Connector */}
            <div className="flex flex-col items-center">
              {step.completed ? (
                <CheckCircle className="h-5 w-5 text-success shrink-0" />
              ) : step.active ? (
                <div className="h-5 w-5 rounded-full border-2 border-primary bg-primary/20 shrink-0 flex items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                </div>
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground/30 shrink-0" />
              )}
              {!isLast && (
                <div className={cn(
                  "w-px flex-1 min-h-[28px]",
                  step.completed ? "bg-success/40" : "bg-border/60"
                )} />
              )}
            </div>

            {/* Content */}
            <div className={cn("pb-5 flex-1 min-w-0", isLast && "pb-0")}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className={cn(
                    "text-sm font-medium leading-5",
                    step.completed && "text-foreground",
                    step.active && "text-primary font-semibold",
                    !step.completed && !step.active && "text-muted-foreground/60"
                  )}>
                    {step.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                </div>
                {step.readinessStatus && step.readinessStatus !== "ready" && (
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] shrink-0",
                      step.readinessStatus === "at_risk" && "border-warning/40 text-warning bg-warning/5",
                      step.readinessStatus === "blocked" && "border-destructive/40 text-destructive bg-destructive/5"
                    )}
                  >
                    {step.readinessStatus === "at_risk" ? "At Risk" : "Blocked"}
                  </Badge>
                )}
              </div>

              {/* Risk */}
              {step.risk && (
                <div className="flex items-center gap-1.5 mt-2 p-2 rounded-lg bg-warning/5 border border-warning/10">
                  <AlertTriangle className="h-3 w-3 text-warning shrink-0" />
                  <p className="text-[11px] text-foreground">{step.risk}</p>
                </div>
              )}

              {/* Confirmation */}
              {step.confirmation && (
                <div className="flex items-center gap-1.5 mt-2 p-2 rounded-lg bg-primary/5 border border-primary/10">
                  <Shield className="h-3 w-3 text-primary shrink-0" />
                  <p className="text-[11px] text-primary font-medium">{step.confirmation}</p>
                </div>
              )}

              {/* Crew */}
              {step.crewAssignment && step.active && (
                <div className="flex items-center gap-1.5 mt-2">
                  <User className="h-3 w-3 text-muted-foreground/60" />
                  <p className="text-[11px] text-muted-foreground">{step.crewAssignment}</p>
                </div>
              )}

              {/* Timestamp */}
              {step.timestamp && step.completed && (
                <div className="flex items-center gap-1 mt-1.5">
                  <Clock className="h-2.5 w-2.5 text-muted-foreground/40" />
                  <p className="text-[10px] text-muted-foreground/50">
                    {new Date(step.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
